import type { SearchEngine } from '../credentials';
import type { SearchDescriptor } from '../descriptor';
import type { Predicate } from '../predicate';
import { and, eq, eqOpt, inList, neq } from '../predicate';
import { notTemplate } from './scopeFilters';

export type OrderSearchType = 'PurchaseOrder' | 'SalesOrder' | 'BuySellOrder' | 'WorkOrder';

export const TYPESENSE_ORDER_TYPE: Readonly<Record<OrderSearchType, string>> = {
  SalesOrder: 'SALES_ORDER',
  PurchaseOrder: 'PURCHASE_ORDER',
  WorkOrder: 'WORK_ORDER',
  BuySellOrder: 'BUYSELL_ORDER',
};

type OrderSearchStatus =
  | 'DRAFT_CUSTOMER'
  | 'DRAFT'
  | 'PENDING'
  | 'SUBMITTED'
  | 'APPROVED'
  | 'RECEIVING'
  | 'PICKING'
  | 'SHIPPED'
  | 'PAYMENT_RECONCILIATION'
  | 'PENDING_LIQUIDATION'
  | 'PENDING_SETTLEMENT'
  | 'CLOSED'
  | 'CANCELLED'
  | 'ERROR';

interface OrderCustomField {
  readonly key: string;
  readonly value: unknown;
}

export interface OrderDoc {
  readonly objectID: string;
  readonly orderId: number;
  readonly organizationId: number;
  readonly __type: OrderSearchType;
  readonly orderType?: string;
  readonly slug: string;
  readonly slugs: readonly string[];
  readonly status: OrderSearchStatus;
  readonly isTemplate: boolean;
  readonly customerPO?: string;
  readonly customerPOs?: readonly string[];
  readonly salesPerson?: string;
  readonly buyer?: string;
  readonly buyerName?: string;
  readonly vendorSO?: string;
  readonly vendorSOs?: readonly string[];
  readonly vendor?: string;
  readonly customer?: string;
  readonly grower?: string;
  readonly broker?: string;
  readonly subsidiary?: string;
  readonly subsidiaryName?: string;
  readonly shippedFromName?: string;
  readonly shipToName?: string;
  readonly edi?: boolean;
  readonly soldThroughSalesOrderId?: number | null;
  readonly sourceId?: number;
  readonly counterpartyName?: string;
  readonly customFields?: readonly OrderCustomField[];
}

export const ORDER_DESCRIPTOR: SearchDescriptor<OrderDoc> = {
  entity: 'order',
  indexName: { algolia: 'orders', typesense: 'orders' },
  searchable: [
    '__type',
    'slug',
    'slugs',
    'customerPO',
    'customerPOs',
    'salesPerson',
    'buyer',
    'status',
    'vendorSO',
    'vendorSOs',
    'vendor',
    'customer',
    'broker',
    'subsidiary',
    'subsidiaryName',
    'shippedFromName',
    'customFields',
  ],
  filterable: ['isTemplate', 'organizationId', 'edi', 'status', '__type', 'soldThroughSalesOrderId', 'orderType'],
  defaults: { hitsPerPage: 20 },
  algolia: {
    searchableAttributes: [
      { path: ['__type'] },
      { path: ['slug'] },
      { path: ['slugs'] },
      { path: ['customerPO'] },
      { path: ['customerPOs'] },
      { path: ['salesPerson'] },
      { path: ['buyer'] },
      { path: ['status'] },
      { path: ['vendorSO'] },
      { path: ['vendorSOs'] },
      { path: ['vendor'] },
      { path: ['customer'] },
      { path: ['broker'] },
      { path: ['subsidiary'] },
      { path: ['subsidiaryName'] },
      { path: ['shippedFromName'] },
      { path: ['customFields', 'value'], mode: 'unordered' },
    ],
    attributesForFaceting: [
      { path: ['isTemplate'] },
      { path: ['organizationId'], mode: 'filterOnly' },
      { path: ['edi'], mode: 'filterOnly' },
      { path: ['status'], mode: 'searchable' },
      { path: ['__type'], mode: 'searchable' },
    ],
  },
  ranking: {
    typesense: { queryBy: 'slugTokens,slug,referenceTokens,counterpartyName,reference,lineItemsText,notesText' },
  },
  tenantField: 'organizationId',
};

export const ORDER_LINKABLE_TO_SO = (): Predicate<OrderDoc> => inList('status', ['DRAFT', 'SUBMITTED']);

export const ORDER_AT_DOCK = (): Predicate<OrderDoc> => inList('status', ['RECEIVING', 'PICKING']);

export const ORDER_NOT_TEMPLATE = (engine: SearchEngine): Predicate<OrderDoc> =>
  notTemplate<OrderDoc>('isTemplate', engine);

export const ORDER_ATTACHABLE_TO_BILL = (engine: SearchEngine): Predicate<OrderDoc> =>
  and([neq('status', 'CLOSED'), ORDER_NOT_TEMPLATE(engine)]);

/** Algolia stores the type as `__type`; the Typesense writer stores it as `orderType`. */
export const ORDER_OF_TYPE = (engine: SearchEngine, type: OrderSearchType): Predicate<OrderDoc> =>
  engine === 'typesense' ? eq('orderType', TYPESENSE_ORDER_TYPE[type]) : eq('__type', type);

export const PO_SOLD_THROUGH = (salesOrderId: number | undefined, engine: SearchEngine): Predicate<OrderDoc> =>
  and([ORDER_OF_TYPE(engine, 'PurchaseOrder'), eqOpt('soldThroughSalesOrderId', salesOrderId), ORDER_LINKABLE_TO_SO()]);

export interface OrderHit {
  readonly orderId: number;
  readonly type: OrderSearchType;
  readonly slug: string;
  readonly status: string;
  readonly buyer?: string;
  readonly vendor?: string;
  readonly counterparty?: string;
  readonly customerPO?: string;
  readonly vendorSO?: string;
  readonly shipToName?: string;
}

const ORDER_SEARCH_TYPE_BY_TYPESENSE: Readonly<Record<string, OrderSearchType>> = Object.fromEntries(
  Object.entries(TYPESENSE_ORDER_TYPE).map(([type, value]) => [value, type as OrderSearchType])
);

const ORDER_SEARCH_TYPES: ReadonlySet<string> = new Set(Object.keys(TYPESENSE_ORDER_TYPE));

const isOrderSearchType = (value: unknown): value is OrderSearchType =>
  typeof value === 'string' && ORDER_SEARCH_TYPES.has(value);

/** An order document's type: Algolia's `__type`, else the type Typesense's `orderType` names. */
export function orderSearchType(doc: {
  readonly __type?: unknown;
  readonly orderType?: unknown;
}): OrderSearchType | undefined {
  if (isOrderSearchType(doc.__type)) return doc.__type;
  return typeof doc.orderType === 'string' ? ORDER_SEARCH_TYPE_BY_TYPESENSE[doc.orderType] : undefined;
}

/** Where an order document names its counterparty, in precedence order: Typesense fills `counterpartyName`, while an Algolia record carries the party under its order type's own field (sales `buyer`, buy-sell `customer` then `vendor`, purchase `vendor`, grower product `grower`) and writes the others empty. */
export const ORDER_COUNTERPARTY_FIELDS = ['counterpartyName', 'buyer', 'customer', 'vendor', 'grower'] as const;

export type OrderCounterpartyField = (typeof ORDER_COUNTERPARTY_FIELDS)[number];

/** The first counterparty field an order document fills with a non-empty name. */
export const orderCounterpartyField = (
  doc: Readonly<Partial<Record<OrderCounterpartyField, unknown>>>
): OrderCounterpartyField | undefined =>
  ORDER_COUNTERPARTY_FIELDS.find((field) => {
    const value = doc[field];
    return typeof value === 'string' && value.length > 0;
  });

export function toOrderHit(doc: OrderDoc): OrderHit | null {
  const orderId = doc.orderId ?? doc.sourceId;
  const type = orderSearchType(doc);
  if (typeof orderId !== 'number' || type === undefined) return null;

  const buyer = doc.buyer ?? doc.buyerName;
  const vendor = doc.vendor ?? (type === 'PurchaseOrder' ? doc.counterpartyName : undefined);
  const counterpartyField = orderCounterpartyField(doc);
  const counterparty = counterpartyField === undefined ? undefined : doc[counterpartyField];
  return {
    orderId,
    type,
    slug: doc.slug,
    status: doc.status,
    ...(buyer === undefined ? {} : { buyer }),
    ...(vendor === undefined ? {} : { vendor }),
    ...(counterparty === undefined ? {} : { counterparty }),
    ...(doc.customerPO === undefined ? {} : { customerPO: doc.customerPO }),
    ...(doc.vendorSO === undefined ? {} : { vendorSO: doc.vendorSO }),
    ...(doc.shipToName === undefined ? {} : { shipToName: doc.shipToName }),
  };
}
